import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { instance } from '~/utils/api/api';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { TransactionsTable } from './TransactionsTable/TransactionsTable';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { InputBar, Pagination, Spinner } from '~/ui';
import '~/assets/css/select-table.css';
import styles from './TransactionsBoard.module.scss';
import FoodExportActions from '~/components/FoodExportActions/FoodExportActions';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { Modal } from '~/components/Shared/Modal/Modal';
import { useToastError } from '~/utils/useToastError';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import TransactionsDishesModalContent from '../TransactionsDishesModalContent/TransactionsDishesModalContent';
import { getPaginate } from '~/utils/getPaginate';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';
import Actions, { Action } from '~/ui/Actions';
import Dropdown from '~/components/Shared/Dropdown';
import { useToastSuccess } from '~/utils/useToastSuccess';

interface SelectedPage {
  selected: number;
}

interface FoodPoints {
  page: number;
}

export const TransactionsBoard = () => {
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<FoodPoints>({
      initialValues: { page: 15 },
      key: 'transactions-points',
      exclude: [],
    });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { paginate } = getPaginate();
  const [page, setPage] = useState<number>(paginate);
  const [searchTerm, setSearchTerm] = useState('');
  const [dates, setDates] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(1, 'week'),
    dayjs(),
  ]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();
  const { user } = useContext(AuthenticationContext);
  const isDeleteAvailable = checkIfUserSuperAdmin(user?.data.role_name);
  const { t } = useTranslation();

  const params = {
    sort: sort.direction,
    dates: [
      dayjs(dates[0])?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      dayjs(dates[1])?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    ],
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
  };

  const {
    data: transactions,
    isError,
    error,
    isFetching,
    refetch,
  } = useQuery({
    queryFn: async () => {
      const response = await instance.get(`admin/food-orders`, {
        params,
      });
      return response.data;
    },
    queryKey: ['transactions', params],
    keepPreviousData: false,
  });

  const { data: qrInfo, isLoading: isQrInfoLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/kso-order-statistic', {
        params: {
          asset_ids: [user?.data.asset_id],
          dates: [
            dayjs(dates[0])?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            dayjs(dates[1])?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          ],
        },
      });
      return data;
    },
    queryKey: ['kso-order-statistic', user?.data.asset_id, dates],
  });

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredTransactions = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredTransactions);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === transactions?.data.length) {
      setSelectedItems([]);
    } else {
      const transactionIdArray = transactions?.data.map(
        (transaction: any) => transaction.id
      );
      setSelectedItems(transactionIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('admin/delete-food-orders', {
        data: { order_ids: selectedItems },
      });
      setSelectedItems([]);
      client.invalidateQueries(['transactions']);
    } catch (error) {
      toastError(error);
    }
  };

  const handleSendSelectedId = async () => {
    try {
      await instance.post('admin/send-order-to-box-office', {
        order_ids: selectedItems,
      });
      setSelectedItems([]);
      toastSuccess(t('orders_shipped_successfully'));
      client.invalidateQueries(['transactions']);
    } catch (error) {
      toastError(error);
    }
  };

  let content: React.ReactNode;

  if (transactions?.data) {
    content = (
      <TransactionsTable
        transactions={transactions?.data}
        sort={sort}
        setSort={setSort}
        handleToggleAll={handleToggleAll}
        handleSelect={handleSelect}
        selectedItems={selectedItems}
      />
    );
  } else if (isError) {
    content = <ErrorMessage error={error} />;
  } else {
    content = <Spinner />;
  }

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  useEffect(() => {
    refetch();
  }, [dates[0], dates[1]]);

  useEffect(() => {
    setPage(paginate);
  }, [paginate]);

  return (
    <div className={styles.transactionsContainer}>
      {qrInfo && !isQrInfoLoading ? (
        <>
          <p className={styles.qrInfo}>
            {t('qr_generations')} ({qrInfo.total})
          </p>

          <Board>
            <div className={styles.transactionsOptions}>
              <TableSelect perPage={perPage} setPerPage={setPerPage} />

              <InputBar
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                value={searchTerm}
                placeholder={t('search_by_id_n_order_np')}
                classNameInput={styles.input}
                label={`${t('search')}:`}
                star={false}
                classNameInputWrapper={styles.inputWrapper}
              />
              <div className={styles.tableHeadRightWrapper}>
                {!!selectedItems.length && isDeleteAvailable && (
                  <Dropdown caption={t('actions')}>
                    <Actions>
                      <Action
                        onClick={handleSendSelectedId}
                        className={styles.sendBtn}
                      >
                        {t('send_orders')}
                      </Action>
                      <Action
                        onClick={handleDeleteSelected}
                        className={styles.deleteButton}
                      >
                        {t('delete')}
                      </Action>
                    </Actions>
                  </Dropdown>
                )}
                <CustomDatePicker
                  value={dates}
                  disabled={isFetching}
                  className={styles.datePicker}
                  showPresets={true}
                  placeholder={[t('date_validate'), t('date_validate')]}
                  onDatesChange={newDates => {
                    setDates(newDates || [dayjs().subtract(6, 'month'), dayjs()]);
                  }}
                />
                <FoodExportActions
                  onOpenDishesClick={() => setIsModalOpen(true)}
                  dates={dates}
                />
              </div>
            </div>
            {content}
            <div className={styles.pagination}>
              <TableAmountPage
                firstRow={transactions?.from}
                lastRow={transactions?.to}
                total={transactions?.total}
              />
              <Pagination
                forcePage={paginate}
                pageCount={transactions?.last_page}
                onPageChange={(selectedPage: SelectedPage) => {
                  setPage(selectedPage.selected + 1);
                }}
              />
            </div>
            <Modal
              size="full"
              modalStyles={styles.modal}
              onClose={() => setIsModalOpen(false)}
              isOpen={isModalOpen}
            >
              <TransactionsDishesModalContent
                onCloseClick={() => setIsModalOpen(false)}
              />
            </Modal>
          </Board>
        </>
      ) : (
        <Spinner />
      )}
    </div>
  );
};
